/**
 * Alerts API Service
 *
 * Service functions for interacting with the alerts API endpoints.
 */

import { apiClient } from "./api";
import type {
  Alert,
  AlertQuery,
  AlertChannels,
  AlertCategory,
  AlertPriority,
  AlertCondition,
  AlertStatus,
} from "@/types/alerts";

const API_PREFIX = "/api/v1/alerts";

/**
 * Backend response type (matches AlertRead schema)
 */
export interface AlertBackend {
  id: number;
  user_id: number;
  name: string;
  description: string | null;
  query: AlertQuery;
  category: string | null;
  custom_category_name: string | null;
  priority: string;
  condition: string;
  condition_value: number | null;
  channels: AlertChannels;
  status: string;
  last_checked: string | null;
  trigger_count: number;
  created_at: string;
  updated_at: string | null;
}

/**
 * Paginated response from backend
 */
export interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Filters for listing alerts
 */
export interface AlertFilters {
  page?: number;
  items_per_page?: number;
  status?: AlertStatus;
}

/**
 * Create data for alerts
 */
export interface AlertCreateData {
  name: string;
  description?: string | null;
  query: AlertQuery;
  category?: AlertCategory | null;
  custom_category_name?: string | null;
  priority: AlertPriority;
  condition: AlertCondition;
  condition_value?: number | null;
  channels: AlertChannels;
}

/**
 * Update data for alerts
 */
export interface AlertUpdateData {
  name?: string;
  description?: string | null;
  query?: AlertQuery;
  category?: AlertCategory | null;
  custom_category_name?: string | null;
  priority?: AlertPriority;
  condition?: AlertCondition;
  condition_value?: number | null;
  channels?: AlertChannels;
  status?: AlertStatus;
}

/**
 * Build query response
 */
export interface BuildQueryResponse {
  query: AlertQuery;
  explanation: string;
  confidence: number;
  suggestions: string[];
  cost_usd: number;
}

/**
 * Test query response
 */
export interface TestQueryResponse {
  total_count: number;
  sample_traces: Array<{
    id: number;
    timestamp: string | null;
    source: string;
    event_type: string;
    tags: string[];
    severity: string;
    anomaly_score: number;
    metadata: Record<string, unknown>;
    raw_data_preview: string;
  }>;
  time_window_start: string;
  time_window_end: string;
  query_valid: boolean;
  execution_time_ms: number;
  warnings: string[];
}

/**
 * Transform backend response to frontend Alert type
 */
function transformToAlert(backend: AlertBackend): Alert {
  return {
    id: backend.id,
    user_id: backend.user_id,
    name: backend.name,
    description: backend.description,
    query: backend.query,
    category: backend.category as AlertCategory | null,
    customCategoryName: backend.custom_category_name,
    priority: backend.priority as AlertPriority,
    condition: backend.condition as AlertCondition,
    conditionValue: backend.condition_value,
    channels: backend.channels,
    status: backend.status as AlertStatus,
    last_checked: backend.last_checked,
    triggerCount: backend.trigger_count,
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
  };
}

/**
 * List alerts for the current user with optional filters.
 */
export async function listAlerts(filters?: AlertFilters): Promise<PaginatedResponse<Alert>> {
  const params = new URLSearchParams();

  if (filters?.page) params.append("page", filters.page.toString());
  if (filters?.items_per_page) params.append("items_per_page", filters.items_per_page.toString());
  if (filters?.status) params.append("status", filters.status);

  const queryString = params.toString();
  const url = `${API_PREFIX}${queryString ? `?${queryString}` : ""}`;

  const response = await apiClient.get<PaginatedResponse<AlertBackend>>(url);

  return {
    ...response,
    data: response.data.map(transformToAlert),
  };
}

/**
 * Get a single alert by ID.
 */
export async function getAlert(id: number): Promise<Alert> {
  const response = await apiClient.get<AlertBackend>(`${API_PREFIX}/${id}`);
  return transformToAlert(response);
}

/**
 * Create a new alert.
 */
export async function createAlert(data: AlertCreateData): Promise<Alert> {
  const response = await apiClient.post<AlertBackend>(API_PREFIX, data);
  return transformToAlert(response);
}

/**
 * Update an alert.
 */
export async function updateAlert(id: number, data: AlertUpdateData): Promise<Alert> {
  const response = await apiClient.patch<AlertBackend>(`${API_PREFIX}/${id}`, data);
  return transformToAlert(response);
}

/**
 * Delete an alert (soft delete).
 */
export async function deleteAlert(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`${API_PREFIX}/${id}`);
}

/**
 * Build an AlertQuery from natural language using AI.
 */
export async function buildQuery(
  naturalLanguage: string,
  includeExamples: boolean = true
): Promise<BuildQueryResponse> {
  return apiClient.post<BuildQueryResponse>(`${API_PREFIX}/build-query`, {
    natural_language_query: naturalLanguage,
    include_examples: includeExamples,
  });
}

/**
 * Test an AlertQuery against the database.
 */
export async function testQuery(
  query: AlertQuery,
  timeWindowHours: number = 24,
  sampleLimit: number = 10
): Promise<TestQueryResponse> {
  return apiClient.post<TestQueryResponse>(`${API_PREFIX}/test-query`, {
    query,
    time_window_hours: timeWindowHours,
    sample_limit: sampleLimit,
  });
}
